package fyi.moko.hotpl.domain.model

/**
 * A public directory (Yellow Page) place entry — the "what is this place" record
 * used for save-time pre-fill / autocomplete. Distinct from [Place] (the user's
 * private journal record): a [YellowPageEntry] is owner-agnostic and shared.
 *
 * Aligned with Google Places (docs/HOTPL_MASTER.md §9 Phase 5): the upstream
 * identifier is [canonicalId] (a Google Place ID), while [entryId] remains the
 * HOTPL-local deterministic key `{geohash}_{normalizedName}`. This lets independent
 * contributions for the same physical place collapse onto one document.
 *
 * The app bulk-caches a region's slice locally and queries it offline-first.
 */
data class YellowPageEntry(
    /** HOTPL-local deterministic id: `{geohash}_{normalizedName}`. */
    val entryId: String,
    /** Upstream/origin identifier — Google Place ID. */
    val canonicalId: String,
    val name: String,
    val normalizedName: String,
    /** Geohash of the location; the coarse prefix is the [regionKey] cache partition. */
    val geohash: String,
    /** Cache-partition key for region bulk download (e.g. a geohash prefix or admin area). */
    val regionKey: String,
    val latitude: Double,
    val longitude: Double,
    val tags: List<String> = emptyList(),
    val phoneNumber: String? = null,
    val website: String? = null,
    /** Cache freshness timestamp; used to decide when to refresh a region slice. */
    val updatedAt: Long = 0L,
    /** Approval status for community-contributed directory entries. */
    val status: String = STATUS_APPROVED,
) {
    companion object {
        const val STATUS_APPROVED = "APPROVED"
        const val STATUS_PENDING = "PENDING"

        /** Geohash precision for a full entry location (~5m cells). */
        const val GEOHASH_PRECISION = 9

        /**
         * Cache-partition key length: the [regionKey] is the first [REGION_KEY_LENGTH]
         * characters of the geohash (~5km × 5km cells). Every producer of an entry
         * (region sync, Google import) must use these same constants or entries land
         * in mismatched partitions and stop deduping.
         */
        const val REGION_KEY_LENGTH = 5

        private val WHITESPACE = Regex("\\s+")

        /**
         * Normalizes a raw place name for matching/dedup: lowercased, trimmed, and
         * all internal whitespace runs collapsed to a single space. Idempotent.
         */
        fun normalizeName(raw: String): String =
            raw.trim().replace(WHITESPACE, " ").lowercase()

        /** Deterministic local id: `"{geohash}_{normalizedName}"`. */
        fun canonicalId(geohash: String, normalizedName: String): String =
            "${geohash}_$normalizedName"

        /** [canonicalId] from a raw (un-normalized) name — normalizes first. */
        fun canonicalIdFromRaw(geohash: String, rawName: String): String =
            canonicalId(geohash, normalizeName(rawName))
    }
}
